<template>
  <div>
    <v-card elevation="0" class="px-7">
      <v-row dense align="center" justify="space-between">
        <v-col cols="12" sm="6">
          <h1 class="fs-16 primary--text font-weight-bold">
            SLA Reports
          </h1>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-breadcrumbs class="app-breadcrumbs py-3" :items="breadcrumbs" />
        </v-col>
      </v-row>
    </v-card>

    <v-container class="px-7" fluid>
      <v-row no-gutters align="center" justify="space-between" class="mb-3">
        <v-col cols="12" sm="6">
          <h1 class="fs-20 font-weight-bold bluedarken--text">
            SLA Reports
          </h1>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn color="primary" icon large>
            <v-icon
              @click="syncData"
              class="icon-large icon-bold"
              color="bluedarken"
            >
              mdi-refresh
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <v-card :loading="loading" class="elevation-card rounded-12 mb-5">
        <v-card-title>
          <div class="mr-2">
            <v-img
              src="@/assets/images/icon-file-text.png"
              width="20"
              height="20"
            />
          </div>
          <span class="fs-14 font-weight-bold primary2--text">
            SLA Reports
          </span>
          <v-spacer />
          <dialog-import-cbs-request />
          <select-column-filter v-model="columnActive" :headers="headers" />
          <v-btn
            depressed
            dark
            color="menuActive"
            class="btn-medium"
            @click="download"
          >
            Download
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-responsive>
            <v-simple-table
              fixed-header
              class="table-taxonomy nav-table sshcf-table"
            >
              <template v-slot:default>
                <VuePerfectScrollbar
                  class="drawer-menu--scroll"
                  :settings="scrollSettings"
                >
                  <thead>
                    <tr>
                      <template v-for="(header, index) in headers">
                        <th
                          v-if="isHeaderSelected(header)"
                          :key="header.text"
                          class="white--text"
                          :class="{
                            [`text-${header.align}`]: true,
                            [header.customClass]: !!header.customClass
                          }"
                        >
                          <span>{{ header.text }}</span>
                          <filter-column
                            v-if="header.value !== 'action'"
                            :indexItem="index"
                            :field="header.value"
                            :listFilter="getFiltered(header.value, index)"
                            @sortAsc="getNestedSort"
                            @searchColumn="searchCln"
                            @filterItem="searchCln"
                          />
                        </th>
                      </template>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in filteredItems" :key="index">
                      <template v-for="header in headers">
                        <td
                          v-if="isColumnSelected(header.value)"
                          :key="header.name"
                          :class="{
                            [header.tdClass]: true,
                            [`text-${header.align}`]: true,
                            [header.tdClass]: !!header.tdClass
                          }"
                        >
                          <router-link
                            v-if="header.linkable"
                            :to="`/sshcf/request/${item.form_id}`"
                            class="bluedarken--text text-link"
                          >
                            {{ item.hcf_code }}
                          </router-link>
                          <template v-else-if="header.value === 'action'">
                            <v-btn
                              text
                              color="primary"
                              class="text-capitalize font-weight-bold"
                              :to="`/sshcf/sla/${item.form_id}`"
                            >
                              <v-img
                                src="@/assets/images/icon-doc.png"
                                width="16"
                                height="16"
                                class="mr-2"
                              />
                              Details
                            </v-btn>
                          </template>
                          <span v-else>
                            {{ header | tableRowValue(item) }}
                          </span>
                        </td>
                      </template>
                    </tr>
                    <tr v-if="!filteredItems.length">
                      <td :colspan="headers.length" class="text-center">
                        {{ loading ? 'Loading...' : 'No data available' }}
                      </td>
                    </tr>
                  </tbody>
                </VuePerfectScrollbar>
              </template>
            </v-simple-table>
          </v-responsive>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import FilterColumn from '@/components/filterColumn';
import { exportToExcel } from '@/utils/helpers';
import {
  exportValue,
  formatDate,
  toCurrencyWithoutSign
} from '@/mixins/filter';
import SelectColumnFilter from '@/components/SelectColumnFilter';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import moment from 'moment';
import DialogImportCbsRequest from '@/components/SSHCF/DialogImportCbsRequest';

const formatter = function(value) {
  return value ? formatDate(value) : '';
};

export default {
  name: 'SLAReports',
  components: {
    DialogImportCbsRequest,
    SelectColumnFilter,
    FilterColumn,
    VuePerfectScrollbar
  },
  data: () => ({
    scrollSettings: {
      maxScrollbarLength: 340
    },
    breadcrumbs: [
      {
        text: 'SSHCF',
        to: '/sshcf/'
      },
      {
        text: 'SLA Reports',
        to: '/sshcf/sla/reports'
      }
    ],
    headers: [
      {
        text: 'HCF Code',
        align: 'left',
        value: 'hcf_code',
        linkable: true,
        customClass: 'sticky-header1',
        tdClass: 'sticky-body1'
      },
      {
        text: 'Preval Number',
        align: 'left',
        value: 'preval_number'
        // tdClass: 'bluedarken--text font-weight-600'
      },
      {
        text: 'IO Number',
        align: 'left',
        value: 'io_number'
        // tdClass: 'bluedarken--text font-weight-600'
      },
      {
        text: 'House',
        align: 'left',
        value: 'house'
      },
      {
        text: 'Regional',
        align: 'left',
        value: 'region'
      },
      {
        text: 'District',
        align: 'left',
        value: 'district'
      },
      {
        text: 'Vendor Code',
        align: 'left',
        value: 'vendor_code'
      },
      {
        text: 'Hospital Name',
        align: 'left',
        value: 'hospital'
      },
      {
        text: 'Amount of Support',
        align: 'left',
        value: 'support_amount',
        formatter: toCurrencyWithoutSign,
        exportFormat: val => +val
      },
      {
        text: 'Created Date',
        align: 'center',
        value: 'created_date'
      },
      {
        text: 'Submitted Date',
        align: 'center',
        value: 'submitted_date'
      },
      {
        text: 'Fully Approved Date',
        align: 'center',
        value: 'approved_date'
      },
      {
        text: 'Total Days Approve',
        align: 'center',
        value: 'total_days_closing'
      },
      {
        text: 'Submitted to Close Days',
        align: 'center',
        value: 'submitted_days_close'
      },
      {
        text: 'Status',
        align: 'center',
        value: 'finance_approval_status'
      },
      {
        text: 'Date Posting',
        align: 'center',
        value: 'finance_approval_posting_date',
        formatter: formatDate
      },
      {
        text: 'Date Due',
        align: 'center',
        value: 'finance_approval_due_date',
        formatter: formatDate
      },
      {
        text: 'Action',
        align: 'center',
        value: 'action'
      }
    ],
    items: [],
    filter: null,
    columnActive: [],
    loading: false
  }),
  computed: {
    filteredItems() {
      return this.getFilteredItems();
    },
    isSelectedAll: {
      get() {
        return this.headers
          ? this.columnActive.length === this.headers.length
          : false;
      },
      set(value) {
        const selected = [];

        if (value) {
          this.headers.forEach(function(user) {
            selected.push(user);
          });
        }

        this.columnActive = selected;
      }
    }
  },
  async mounted() {
    await this.selectAllColumn();
    this.fetch();
  },
  methods: {
    syncData() {
      this.loading = true;
      this.$store.dispatch('sshcf/syncData', 'form_request');
      this.fetch();
    },
    selectAllColumn() {
      this.columnActive = this.headers;
    },
    async getDetail(id) {
      this.loadingDetail = true;
      await this.$store.dispatch('sshcf/getSLAReportDetail', id);
      this.loadingDetail = false;
    },
    async fetch(params = {}) {
      this.loading = true;
      const res = await this.$store.dispatch('sshcf/getSLAReports', {
        ...params
      });
      this.loading = false;
      this.items = res.data.map(item => ({
        ...item,
        created_date: formatter(item.created_date),
        submitted_date: formatter(item.submitted_date),
        approved_date: formatter(item.approved_date),
        finance_approval_status: item.finance_approval?.status,
        finance_approval_posting_date: item.finance_approval?.posting_date,
        finance_approval_due_date: item.finance_approval?.due_date
      }));
      this.totalItems = res.data.total_data;
    },
    onRowClicked(item) {
      this.getDetail(item.form_id);
    },
    toggleSelectAll() {
      this.isSelectAll = !this.isSelectAll;
    },
    isHeaderSelected(header) {
      return this.columnActive.map(item => item.value).includes(header.value);
    },
    isColumnSelected(name) {
      return this.columnActive.filter(item => item.value === name).length > 0;
    },
    getAscSort(obj) {
      this.items.sort(this.dynamicSort(obj.fieldName));
    },
    getNestedSort(obj) {
      this.nestedSort(obj.fieldName, this.items);
    },
    searchCln(obj) {
      this.filter = obj;
    },
    getFilteredItems() {
      const obj = this.filter;
      let arr = this.items;

      if (!obj) return arr;

      let nameField = obj.fieldName;
      let valSearch = Array.isArray(obj.value)
        ? obj.value.join('|').toLowerCase()
        : obj.value.toLowerCase();
      let result = [];
      if (!nameField.includes('.')) {
        /*search array of object*/
        if (nameField === 'start') {
          nameField = 'dateStart';
        } else if (nameField === 'end') {
          nameField = 'dateEnd';
        }
        result = arr.filter(function(a) {
          if (a[nameField] !== null) {
            if (Array.isArray(obj.value)) {
              return obj.value.includes(String(a[nameField]));
            } else {
              return a[nameField]
                .toString()
                .toLowerCase()
                .match(valSearch);
            }
          }
        });
      } else {
        /*search array of nested object*/
        let arrObj = nameField.split('.');
        if (arrObj.length === 2) {
          result = arr.filter(function(a) {
            if (a[arrObj[0]][arrObj[1]] !== null) {
              if (Array.isArray(obj.value)) {
                return obj.value.includes(a[arrObj[0]][arrObj[1]].toString());
              } else {
                return a[arrObj[0]][arrObj[1]]
                  .toString()
                  .toLowerCase()
                  .match(valSearch);
              }
            }
          });
        } else if (arrObj.length === 3) {
          result = arr.filter(function(a) {
            if (a[arrObj[0]][arrObj[1]][arrObj[2]] !== null) {
              if (Array.isArray(obj.value)) {
                return obj.value.includes(
                  a[arrObj[0]][arrObj[1]][arrObj[2]].toString()
                );
              } else {
                return a[arrObj[0]][arrObj[1]][arrObj[2]]
                  .toString()
                  .toLowerCase()
                  .match(valSearch);
              }
            }
          });
        } else if (arrObj.length === 4) {
          result = arr.filter(function(a) {
            if (a[arrObj[0]][arrObj[1]][arrObj[2]][arrObj[3]] !== null) {
              if (Array.isArray(obj.value)) {
                return obj.value.includes(
                  a[arrObj[0]][arrObj[1]][arrObj[2]][arrObj[3]].toString()
                );
              } else {
                return a[arrObj[0]][arrObj[1]][arrObj[2]][arrObj[3]]
                  .toString()
                  .toLowerCase()
                  .match(valSearch);
              }
            }
          });
        } else {
          result = this.items;
        }
      }

      return result;
    },
    getFiltered(name) {
      if (!name.includes('.')) {
        return Object.keys(
          this.items.reduce((result, arr) => {
            result[arr[name]] = [...(result[arr[name]] || []), arr];
            return result;
          }, {})
        );
      } else {
        let nameField = name.split('.');
        return Object.keys(
          this.items.reduce((result, arr) => {
            if (!arr[nameField[0]]) return;
            result[arr[nameField[0]][nameField[1]]] = [
              ...(result[arr[nameField[0]][nameField[1]]] || []),
              arr
            ];
            return result;
          }, {})
        );
      }
    },
    download() {
      const headers = this.headers.filter(item => item.value !== 'action');
      const columns = headers.map(item => item.text);
      const items = this.filteredItems.map(item =>
        headers.map(header => exportValue(header, item) || '')
      );
      const date = moment().format('YYYY-MM-DD-HHmmss');
      exportToExcel(columns, items, 'SLA-Reports-' + date);
    }
  }
};
</script>

<style scoped>
.drawer-menu--scroll {
  max-height: 600px;
  overflow: auto;
}
</style>
